import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, Flag, Heart, Youtube } from 'lucide-react';
import {
  CARD_TYPE_LABELS,
  LANGUAGE_NAMES,
  sourceContentOf,
  sourceLanguageOf,
  type ContentCard,
  type SupportedLanguage,
} from '@/types/content';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ContentPreviewProps {
  card: ContentCard;
  /** The saved card (Edit page). Used to tell the admin when the shown translations are older than the typed text. */
  savedCard?: ContentCard;
}

/** Source language first, then the others in LANGUAGE_NAMES order (VAP-835). */
function previewLanguageOrder(card: ContentCard): SupportedLanguage[] {
  const source = sourceLanguageOf(card);
  const all = Object.keys(LANGUAGE_NAMES) as SupportedLanguage[];
  return [source, ...all.filter(l => l !== source)];
}

/**
 * A phone-sized render of the engagement card. The app itself renders the card
 * white on a light screen regardless of the admin's theme, so the phone keeps
 * fixed light colours on purpose.
 */
export function ContentPreview({ card, savedCard }: ContentPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Language stepping (VAP-835). The source language is first and the default.
  const languages = useMemo(() => previewLanguageOrder(card), [card]);
  const [languageIndex, setLanguageIndex] = useState(0);
  const sourceLanguage = sourceLanguageOf(card);
  useEffect(() => {
    // The source language changed (Create page selector): go back to it
    setLanguageIndex(0);
  }, [sourceLanguage]);
  const language = languages[Math.min(languageIndex, languages.length - 1)];
  const isSource = language === sourceLanguage;
  const translation = card.content[language];
  // A language with no translation shows the source text, marked as not translated
  const content = translation ?? sourceContentOf(card);
  // 'off': a saved card whose translations were switched off at creation, so nothing is pending.
  // An unsaved card (Create page) has no status yet and reads as 'missing'.
  const translationsOff = card.translationStatus === 'NONE';
  const translationState: 'source' | 'ready' | 'failed' | 'missing' | 'off' = isSource
    ? 'source'
    : translation
      ? 'ready'
      : card.translationProgress?.[language] === 'failed'
        ? 'failed'
        : translationsOff
          ? 'off'
          : 'missing';
  const stepLanguage = (delta: number) => setLanguageIndex(i => (i + delta + languages.length) % languages.length);

  // Edit page: the typed source text is newer than the stored translations
  const sourceTextChanged = Boolean(
    savedCard && JSON.stringify(sourceContentOf(card)) !== JSON.stringify(sourceContentOf(savedCard))
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border bg-muted/40 py-4">
        <CardTitle>Preview</CardTitle>
        <CardDescription>How the card looks in the app. Tap the card to collapse or expand it.</CardDescription>
        <div
          className="mt-3 flex items-center justify-between gap-2"
          aria-label="Preview language"
          // Arrow keys step when either button has focus (the event bubbles up from the button)
          onKeyDown={e => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); stepLanguage(-1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); stepLanguage(1); }
          }}
        >
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => stepLanguage(-1)} aria-label="Previous language">
            <ChevronLeft />
          </Button>
          <span className="flex min-w-0 flex-col items-center text-sm" aria-live="polite">
            <span className="font-medium">
              {LANGUAGE_NAMES[language]} <span className="text-muted-foreground">({language})</span>
              {isSource && <span className="ml-1.5 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">written</span>}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {languageIndex + 1} / {languages.length}
              {translationState === 'ready' && ' · translated'}
              {translationState === 'missing' && ' · not translated yet'}
              {translationState === 'off' && ' · translations off'}
              {translationState === 'failed' && <span className="text-destructive"> · translation failed</span>}
            </span>
          </span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => stepLanguage(1)} aria-label="Next language">
            <ChevronRight />
          </Button>
        </div>
      </CardHeader>

      <div className="flex justify-center bg-gradient-to-b from-muted/30 to-muted/70 p-6">
        {/* Phone frame */}
        <div className="w-full max-w-[320px] rounded-[2rem] border-[6px] border-neutral-800 bg-neutral-100 shadow-xl">
          <div className="flex items-center justify-between px-5 pt-3 text-[11px] font-medium text-neutral-700">
            <span>9:41</span>
            <span className="h-4 w-20 rounded-full bg-neutral-800" aria-hidden />
            <span className="flex items-center gap-1" aria-hidden>
              <span className="h-2 w-3 rounded-sm bg-neutral-600" />
              <span className="h-2.5 w-5 rounded-sm bg-neutral-600" />
            </span>
          </div>

          <div className="px-3 pb-4 pt-3">
            <div className="overflow-hidden rounded-2xl bg-white text-neutral-900 shadow-sm">
              <button type="button" className="w-full p-4 text-left" onClick={() => setIsExpanded(v => !v)} aria-expanded={isExpanded}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                        {CARD_TYPE_LABELS[card.cardType]}
                      </span>
                      {card.isPushFlagged && <Flag className="h-3 w-3 text-accent" aria-label="Push flagged" />}
                    </div>
                    <h4 className="text-sm font-semibold leading-snug">{content?.title || 'Untitled'}</h4>
                    {!isExpanded && (
                      <p className="mt-1 line-clamp-2 text-xs text-neutral-500">{content?.summary || content?.title}</p>
                    )}
                  </div>
                  <span className="p-1 text-neutral-400">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="space-y-3 px-4 pb-4">
                  {card.mediaUrls && card.mediaUrls.length > 0 && (
                    <div className="space-y-2">
                      {card.mediaUrls.map((media, index) => (
                        <div key={`${media.url}-${index}`} className="aspect-video overflow-hidden rounded-lg bg-neutral-100">
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt=""
                              className="h-full w-full object-cover"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center gap-2 bg-neutral-900 text-xs text-white">
                              <Youtube className="h-4 w-4 text-red-500" />
                              YouTube video
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="whitespace-pre-wrap text-sm text-neutral-700">{content?.body || <span className="text-neutral-400">Body text appears here.</span>}</p>

                  {card.externalLinks && card.externalLinks.length > 0 && (
                    <div className="space-y-1.5">
                      {card.externalLinks.map((link, index) => (
                        <span key={`${link.url}-${index}`} className="flex items-center gap-2 text-sm text-primary">
                          <ExternalLink className="h-3 w-3" />
                          {link.label[language] || link.label.en}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-neutral-100 pt-2 text-neutral-400">
                    <span className="flex items-center gap-1 text-xs">
                      <Heart className="h-4 w-4" />
                      {card.metrics.likeCount}
                    </span>
                    <span className="text-[11px]">{card.publishDate}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-center">
              <span className="h-1 w-28 rounded-full bg-neutral-400" aria-hidden />
            </div>
          </div>
        </div>
      </div>

      {(translationState !== 'source' && translationState !== 'ready') || sourceTextChanged ? (
        <div className="space-y-1 border-t border-border px-6 py-3 text-xs text-muted-foreground">
          {translationState === 'off' && (
            <p>This card has no translations. The phone shows the {LANGUAGE_NAMES[sourceLanguage]} text in every language.</p>
          )}
          {translationState === 'missing' && (
            <p>{LANGUAGE_NAMES[language]} is not translated yet. The phone shows the {LANGUAGE_NAMES[sourceLanguage]} text until the translation is ready.</p>
          )}
          {translationState === 'failed' && (
            <p className="text-destructive">The {LANGUAGE_NAMES[language]} translation failed. The phone shows the {LANGUAGE_NAMES[sourceLanguage]} text. Use Retry above.</p>
          )}
          {sourceTextChanged && <p>The other languages show the translations from the last save. Save to translate the new text.</p>}
        </div>
      ) : null}
    </Card>
  );
}
